import { ServerDomainInfo } from '@/api/type-server'
import { useUserStore } from '@/stores/user'

import { BarSegment, ColorScheme, SegmentedProgressBar } from './SegmentedProgressBar'
import { SelectedData } from './volume-quota-modal'
import { getDatadriveGradientClass } from './volume-usage-segmented-bar'

interface VolumeQuotaSegmentedBarProps {
  summarizedData: SelectedData
  totalQuota?: number // Optional: external quota (for team modals)
  imageOnSpaceBilling?: boolean
  snapshotOnSpaceBilling?: boolean
  domainInfos?: ServerDomainInfo[]
}

export function VolumeQuotaSegmentedBar({
  summarizedData,
  totalQuota,
  imageOnSpaceBilling = true,
  snapshotOnSpaceBilling = true,
  domainInfos,
}: VolumeQuotaSegmentedBarProps) {
  const userStore = useUserStore()

  // Determine quota and color scheme
  let totalQuotaInGB: number
  let colorScheme: ColorScheme

  if (totalQuota !== undefined) {
    // External quota provided (e.g., team modals) - always use normal colors
    totalQuotaInGB = totalQuota
    colorScheme = 'normal'
  } else if (userStore.isOrderExpired) {
    // User order expired
    totalQuotaInGB = userStore.partVolume?.quota_gb || 0
    colorScheme = 'expired'
  } else if (!userStore.order) {
    // No package
    totalQuotaInGB = userStore.partVolume?.quota_gb || 0
    colorScheme = 'noPackage'
  } else {
    // Normal user quota
    totalQuotaInGB = userStore.partVolume?.quota_gb || 0
    colorScheme = 'normal'
  }

  const countQuota = (quotasInGB?: Array<number>) => {
    if (!quotasInGB) return 0
    return quotasInGB.reduce((acc, vol) => {
      return acc + vol
    }, 0)
  }

  const snapshotTotalQuota = countQuota(
    summarizedData.selectedData.filter((i) => i.volume.type === 2).map((i) => i.selectedQuotaInGB),
  )
  const cloudTotalQuota = countQuota(
    summarizedData.selectedData.filter((i) => i.volume.type !== 2).map((i) => i.selectedQuotaInGB),
  )

  if (imageOnSpaceBilling && snapshotOnSpaceBilling) {
    const snapshotQuota = totalQuotaInGB > 0 ? snapshotTotalQuota / totalQuotaInGB : 0
    const snapshotAndVolumeQuota =
      totalQuotaInGB > 0 ? (snapshotTotalQuota + cloudTotalQuota) / totalQuotaInGB : 0

    return (
      <SegmentedProgressBar
        value={snapshotQuota}
        secondValue={snapshotAndVolumeQuota}
        colorScheme={colorScheme}
      />
    )
  }

  const expired = colorScheme === 'expired'
  const segments: BarSegment[] = []

  if (snapshotOnSpaceBilling && totalQuotaInGB > 0) {
    segments.push({
      ratio: snapshotTotalQuota / totalQuotaInGB,
      className: expired ? 'bg-bar-tertiary' : 'bg-btn-tertiary',
    })
  }

  if (!imageOnSpaceBilling && !snapshotOnSpaceBilling && totalQuotaInGB > 0) {
    const cloudItems = summarizedData.selectedData.filter((i) => i.volume.type !== 2)
    const orderedItems = domainInfos?.length
      ? domainInfos.map((info) => cloudItems.find((i) => i.volume.domain === info.domain))
      : cloudItems
    orderedItems.forEach((item, index) => {
      if (item && item.selectedQuotaInGB > 0) {
        segments.push({
          ratio: item.selectedQuotaInGB / totalQuotaInGB,
          className: getDatadriveGradientClass(index, expired),
        })
      }
    })
  } else if (totalQuotaInGB > 0) {
    segments.push({
      ratio: cloudTotalQuota / totalQuotaInGB,
      className: expired ? 'bg-bar-tertiary/60' : 'bg-btn-secondary',
    })
  }

  const remainderClassName = imageOnSpaceBilling
    ? expired
      ? 'bg-tertiary'
      : 'bg-active'
    : 'bg-neutral-200'

  return (
    <SegmentedProgressBar
      value={0}
      segments={segments}
      colorScheme={colorScheme}
      remainderClassName={remainderClassName}
    />
  )
}
